import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, signInWithCustomToken, onAuthStateChanged, signOut } from 'firebase/auth';
import apiService from '../services/api';
import app from '../config/firebase'; // Ensure you have firebase app initialized here

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Safely get auth instance
  let auth;
  try {
    auth = getAuth(app);
  } catch (error) {
    console.error('Firebase auth initialization failed:', error);
    auth = null;
  }

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log("Auth state changed - user logged in:", user.email);
          }
          const token = await user.getIdToken();
          if (process.env.NODE_ENV === 'development') {
            console.log("Got ID token, length:", token.length);
          }
          localStorage.setItem('authToken', token);
          if (process.env.NODE_ENV === 'development') {
            console.log("Token stored in localStorage");
          }
          
          // Add a small delay to ensure token is properly stored
          await new Promise(resolve => setTimeout(resolve, 100));
          
          if (process.env.NODE_ENV === 'development') {
            console.log("Calling getCurrentUser...");
          }
          const apiUser = await apiService.getCurrentUser();
          if (process.env.NODE_ENV === 'development') {
            console.log("getCurrentUser response:", apiUser);
          }
          setCurrentUser(apiUser);
        } catch (error) {
          console.error("Authentication error:", error);
          // If anything fails, sign out to be safe
          await signOut(auth);
          setCurrentUser(null);
          localStorage.removeItem('authToken');
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log("Auth state changed - user logged out");
        }
        setCurrentUser(null);
        localStorage.removeItem('authToken');
      }
      // This is the single place where loading should be set to false.
      setLoading(false);
    });

    return unsubscribe;
  }, [auth]);

  const login = async (email, password) => {
    if (!auth) {
      throw new Error('Firebase auth is not available');
    }
    
    // Set loading to true at the start of login
    setLoading(true);
    
    try {
      const response = await apiService.login(email, password);
      const customToken = response.token;
      await signInWithCustomToken(auth, customToken);
      // Let onAuthStateChanged handle the rest, including setting loading to false.
    } catch (error) {
      console.error("Login failed:", error);
      // On failure, ensure loading is turned off.
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    if (!auth) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Firebase auth is not available for logout');
      }
      setCurrentUser(null);
      localStorage.removeItem('authToken');
      return;
    }
    
    // Set loading to true to prevent weird flashes during logout
    setLoading(true);
    try {
      await signOut(auth);
      // onAuthStateChanged will handle clearing user, token, and setting loading to false
    } catch (error) {
      console.error('Logout error:', error);
      // Ensure loading is off even if logout fails
      setLoading(false);
    }
  };

  const value = {
    currentUser,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 